import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { apiGet, apiPatch, apiPut } from "../lib/api.ts";
import { OutlineButton } from "./FilterBar.tsx";
import { theme } from "../theme.ts";
import type { LeadTimeSettings, LeadTimeSettingsResponse } from "@march/shared";

const CATS: Array<{ key: keyof LeadTimeSettings["categories"]; label: string; note: string }> = [
  { key: "Frame", label: "Frames", note: "Overseas" },
  { key: "Base", label: "Bases", note: "Overseas" },
  { key: "Fabric", label: "Fabric", note: "Local" },
  { key: "Cover", label: "Sunbrella covers", note: "Cut and sewn in-house" },
  { key: "ImportedCover", label: "Olefin & Pacifica covers", note: "Bought finished from overseas, like frames" },
  { key: "Trim", label: "Trim", note: "Binding, fringe, tassels (yards); overseas" },
  { key: "ProtectiveCover", label: "Protective covers", note: "Storage covers; overseas" },
];
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

const same = (a: LeadTimeSettings, b: LeadTimeSettings) => JSON.stringify(a) === JSON.stringify(b);

/**
 * Lead-time "dials". Category lead times off season and in season, which
 * months count as season, and per-SKU overrides. Saving recomputes the
 * forecast in the background so Need to order follows within a minute.
 */
export function LeadTimesCard() {
  const qc = useQueryClient();
  const q = useQuery<LeadTimeSettingsResponse>({
    queryKey: ["lead-times"],
    queryFn: () => apiGet<LeadTimeSettingsResponse>("/api/settings/lead-times"),
  });
  const [draft, setDraft] = useState<LeadTimeSettings | null>(null);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const [skuInput, setSkuInput] = useState("");
  const [skuDays, setSkuDays] = useState("");

  useEffect(() => {
    if (q.data && !draft) setDraft(structuredClone(q.data.settings));
  }, [q.data, draft]);

  if (q.isLoading || !draft) return <div style={{ color: theme.color.textMuted }}>Loading…</div>;
  if (q.error) return <div style={{ color: theme.color.danger }}>{(q.error as Error).message}</div>;
  const d = q.data!;
  const dirty = !same(draft, d.settings);

  const setDays = (cat: keyof LeadTimeSettings["categories"], which: "off" | "in", v: string) =>
    setDraft({
      ...draft,
      categories: { ...draft.categories, [cat]: { ...draft.categories[cat], [which]: Number(v) } },
    });
  const toggleMonth = (m: number) =>
    setDraft({
      ...draft,
      season_months: draft.season_months.includes(m)
        ? draft.season_months.filter((x) => x !== m)
        : [...draft.season_months, m].sort((a, b) => a - b),
    });

  const save = async (value: LeadTimeSettings) => {
    setSaving(true);
    setMsg(null);
    try {
      await apiPut("/api/settings/lead-times", value);
      setMsg({ ok: true, text: "Saved. The forecast is recalculating — Need to order updates within about a minute." });
      // Refetch first, then reset the form from the fresh values. Resetting
      // before the refetch re-seeded the form from the stale response and
      // left it looking unsaved.
      await qc.invalidateQueries({ queryKey: ["lead-times"] });
      setDraft(null);
      setTimeout(() => qc.invalidateQueries({ queryKey: ["forecast"] }), 60_000);
    } catch (e) {
      const text = (e as Error).message;
      const m = text.match(/"errors":\[(.*?)\]/);
      setMsg({ ok: false, text: m ? m[1].replace(/"/g, "").split(",").join(" · ") : text });
    } finally {
      setSaving(false);
    }
  };

  const setOverride = async (itemid: string, days: number | null) => {
    setMsg(null);
    try {
      await apiPatch(`/api/skus/${encodeURIComponent(itemid)}`, { lead_time_days: days });
      setSkuInput("");
      setSkuDays("");
      setMsg({ ok: true, text: `${itemid}: ${days == null ? "override removed" : `${days} days`}. Recalculating.` });
      await qc.invalidateQueries({ queryKey: ["lead-times"] });
    } catch (e) {
      setMsg({ ok: false, text: (e as Error).message });
    }
  };

  const input: React.CSSProperties = {
    width: 64,
    padding: "4px 6px",
    border: `1px solid ${theme.color.border}`,
    borderRadius: theme.radius.sm,
    textAlign: "right",
    fontVariantNumeric: "tabular-nums",
  };
  const th: React.CSSProperties = {
    textAlign: "left",
    fontSize: theme.font.size.xs,
    color: theme.color.textMuted,
    fontWeight: 600,
    padding: "6px 8px",
  };

  return (
    <div style={{ display: "grid", gap: theme.space.lg }}>
      <div style={{ fontSize: theme.font.size.sm, color: theme.color.textMuted, lineHeight: 1.5 }}>
        Days from ordering to usable stock. <b>Need to order</b> and the order / expedite tags on the
        Forecast, To order and Arriving all use these. We order on demand, so stock
        plus everything on order only has to last the lead time.
      </div>

      <table style={{ borderCollapse: "collapse", fontSize: theme.font.size.sm, maxWidth: 560 }}>
        <thead>
          <tr>
            <th style={th}>Category</th>
            <th style={{ ...th, textAlign: "right" }}>Off season</th>
            <th style={{ ...th, textAlign: "right" }}>In season</th>
            <th style={th} />
          </tr>
        </thead>
        <tbody>
          {CATS.map((c) => (
            <tr key={c.key} style={{ borderTop: `1px solid ${theme.color.border}` }}>
              <td style={{ padding: "8px" }}>
                <b>{c.label}</b>
                <div style={{ fontSize: theme.font.size.xs, color: theme.color.textFaint }}>{c.note}</div>
              </td>
              {(["off", "in"] as const).map((w) => (
                <td key={w} style={{ padding: "8px", textAlign: "right", whiteSpace: "nowrap" }}>
                  <input
                    type="number"
                    min={1}
                    max={365}
                    aria-label={`${c.label} ${w === "off" ? "off season" : "in season"} days`}
                    value={draft.categories[c.key][w]}
                    onChange={(e) => setDays(c.key, w, e.target.value)}
                    style={input}
                  />{" "}
                  <span style={{ color: theme.color.textFaint }}>d</span>
                </td>
              ))}
              <td style={{ padding: "8px", fontSize: theme.font.size.xs, color: theme.color.textFaint }}>
                {draft.categories[c.key].off !== d.defaults.categories[c.key].off ||
                draft.categories[c.key].in !== d.defaults.categories[c.key].in
                  ? `default ${d.defaults.categories[c.key].off} / ${d.defaults.categories[c.key].in}`
                  : ""}
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <div>
        <div style={{ fontSize: theme.font.size.sm, fontWeight: 600, marginBottom: 6 }}>In-season months</div>
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
          {MONTHS.map((label, i) => {
            const on = draft.season_months.includes(i + 1);
            return (
              <button
                key={label}
                type="button"
                aria-pressed={on}
                onClick={() => toggleMonth(i + 1)}
                style={{
                  padding: "4px 10px",
                  borderRadius: 999,
                  border: `1px solid ${on ? theme.color.brand : theme.color.border}`,
                  background: on ? theme.color.brandFaint : theme.color.surface,
                  color: on ? theme.color.brand : theme.color.textMuted,
                  fontWeight: on ? 600 : 500,
                  fontSize: theme.font.size.xs,
                  cursor: "pointer",
                }}
              >
                {label}
              </button>
            );
          })}
        </div>
      </div>

      <div style={{ display: "flex", gap: theme.space.sm, alignItems: "center", flexWrap: "wrap" }}>
        <button
          type="button"
          disabled={!dirty || saving}
          onClick={() => save(draft)}
          style={{
            padding: "6px 14px",
            borderRadius: theme.radius.md,
            border: "none",
            background: dirty ? theme.color.brand : theme.color.border,
            color: theme.color.onBrand,
            fontWeight: 600,
            cursor: dirty && !saving ? "pointer" : "default",
          }}
        >
          {saving ? "Saving…" : "Save lead times"}
        </button>
        {dirty && (
          <OutlineButton onClick={() => setDraft(structuredClone(d.settings))}>Discard changes</OutlineButton>
        )}
        {!d.is_default && !dirty && (
          <OutlineButton onClick={() => save(d.defaults)} disabled={same(d.settings, d.defaults)}>
            Reset to defaults
          </OutlineButton>
        )}
        <span style={{ fontSize: theme.font.size.xs, color: theme.color.textFaint }}>
          {d.is_default
            ? "Using defaults"
            : `Last changed by ${d.updated_by ?? "unknown"} on ${new Date(d.updated_at!).toLocaleString()}`}
        </span>
      </div>

      {msg && (
        <div
          style={{
            padding: "8px 10px",
            borderRadius: theme.radius.md,
            background: msg.ok ? theme.color.successFaint : theme.color.dangerFaint,
            color: msg.ok ? theme.color.successText : theme.color.dangerText,
            fontSize: theme.font.size.sm,
          }}
        >
          {msg.text}
        </div>
      )}

      <div>
        <div style={{ fontSize: theme.font.size.sm, fontWeight: 600, marginBottom: 4 }}>SKU overrides</div>
        <div style={{ fontSize: theme.font.size.xs, color: theme.color.textMuted, marginBottom: 8 }}>
          A SKU with its own lead time ignores its category and the season.
        </div>
        {d.overrides.length === 0 ? (
          <div style={{ fontSize: theme.font.size.sm, color: theme.color.textFaint }}>None.</div>
        ) : (
          <div style={{ display: "grid", gap: 4, maxWidth: 560 }}>
            {d.overrides.map((o) => (
              <div key={o.itemid} style={{ display: "flex", gap: 10, alignItems: "center", fontSize: theme.font.size.sm }}>
                <span style={{ fontFamily: theme.font.mono, minWidth: 180 }}>{o.itemid}</span>
                <span style={{ color: theme.color.textFaint, minWidth: 70 }}>{o.item_type}</span>
                <b>{o.lead_time_days}d</b>
                <button
                  type="button"
                  onClick={() => setOverride(o.itemid, null)}
                  style={{ all: "unset", cursor: "pointer", color: theme.color.danger, fontSize: theme.font.size.xs }}
                >
                  remove
                </button>
              </div>
            ))}
          </div>
        )}
        <div style={{ display: "flex", gap: 6, marginTop: 10, alignItems: "center", flexWrap: "wrap" }}>
          <input
            placeholder="SKU, e.g. ALTO908-002"
            aria-label="SKU to override"
            value={skuInput}
            onChange={(e) => setSkuInput(e.target.value)}
            style={{ ...input, width: 200, textAlign: "left" }}
          />
          <input
            type="number"
            min={1}
            max={365}
            placeholder="days"
            aria-label="Override days"
            value={skuDays}
            onChange={(e) => setSkuDays(e.target.value)}
            style={input}
          />
          <OutlineButton
            onClick={() => setOverride(skuInput.trim(), Number(skuDays))}
            disabled={!skuInput.trim() || !(Number(skuDays) >= 1)}
          >
            Set override
          </OutlineButton>
        </div>
      </div>

      {d.history.length > 0 && (
        <details>
          <summary style={{ cursor: "pointer", fontSize: theme.font.size.sm, color: theme.color.textMuted }}>
            Change history
          </summary>
          <div style={{ display: "grid", gap: 4, marginTop: 8, fontSize: theme.font.size.xs, color: theme.color.textMuted }}>
            {d.history.map((h, i) => (
              <div key={i}>
                {new Date(h.updated_at).toLocaleString()} · {h.updated_by ?? "unknown"} · {h.what}
                {h.what.startsWith("SKU") ? `: ${h.value}` : ""}
              </div>
            ))}
          </div>
        </details>
      )}
    </div>
  );
}
